/**
 * User Preferences Service
 *
 * Service for managing user preference updates. Extends the base
 * CrudService to provide preference-specific functionality with proper
 * validation and error handling.
 *
 * This service handles PATCH requests to /api/user/preferences for updating
 * user preferences such as theme, measurement system, language, etc.
 */

import { invalidDataErrorFromZod } from '../errors/invalid-data.error';
import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import { updateUserPreferencesSchema } from '../validation/user-preferences.schemas';

import type { InvalidDataError } from '../errors/invalid-data.error';
import type {
  UserPreferences,
  UpdateUserPreferencesRequest,
  UpdateUserPreferencesResponse,
} from '../types/user-preferences.types';
import type { ZodError } from 'zod';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Convert ZodError to InvalidDataError with structured validation errors
 * @param error - The ZodError from validation
 * @returns InvalidDataError with structured validation info
 */
function createValidationError(error: ZodError): InvalidDataError {
  return invalidDataErrorFromZod("Couldn't save your preferences", error, {
    details: {
      service: 'UserPreferencesService',
      operation: 'updatePreferences',
    },
  });
}

// =============================================================================
// USER PREFERENCES SERVICE CLASS
// =============================================================================

/**
 * UserPreferencesService class providing user preference management functionality
 * Extends CrudService to inherit common CRUD operations and adds preference-specific methods
 */
export class UserPreferencesService extends CrudService<
  UserPreferences,
  UserPreferences,
  UserPreferences
> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/user/preferences',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
    });
  }

  // ---------------------------------------------------------------------------
  // PREFERENCE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Update user preferences
   *
   * PATCH /api/user/preferences
   *
   * Accepts a partial preferences object and updates only the provided fields.
   * Only include the fields you want to update.
   * The API returns 204 No Content on success.
   *
   * @param data - Partial preferences data to update (only include fields to update)
   * @returns Promise resolving when update is complete
   * @throws InvalidDataError if validation fails
   * @throws Error if the API request fails
   *
   * @example
   * ```typescript
   * // Update just the theme
   * await userPreferencesService.updatePreferences({
   *   theme_selection: 'dark',
   * });
   *
   * // Update multiple fields
   * await userPreferencesService.updatePreferences({
   *   theme_selection: 'dark',
   *   measurement_system: 'imperial',
   *   workouts_per_week: 3,
   * });
   * ```
   */
  public async updatePreferences(
    data: UpdateUserPreferencesRequest
  ): Promise<UpdateUserPreferencesResponse> {
    // Validate input data
    this.validatePreferencesData(data);

    try {
      const response = await apiClient.patch<void>('/user/preferences', data);

      // API returns 204 No Content on success
      if (response.status !== 204 && response.status !== 200) {
        throw new Error('Failed to update user preferences');
      }

      // Invalidate cache after successful update
      this.invalidateCache(['getAll', 'getById']);
    } catch (error) {
      // Preserve the typed error the coordinator's central error transform
      // produced (an `ApiErrorClass` — including any 402, a server 422
      // `ValidationErrorClass`, and the normalized auth/network failures) so
      // `.status`/`.code`/`.details` survive to the store/UI, and re-throw the
      // client-side `InvalidDataError` from `validatePreferencesData` by
      // identity. Only a genuinely untyped throw (e.g. the non-204 guard above)
      // falls back to the generic message.
      console.error('Failed to update user preferences:', error);
      rethrowTyped(error, 'Failed to update user preferences.');
    }
  }

  // ---------------------------------------------------------------------------
  // VALIDATION METHODS
  // ---------------------------------------------------------------------------

  /**
   * Validate preferences data using Zod schema
   * Ensures all provided fields are valid before API calls
   *
   * @param data - Partial preferences data to validate
   * @throws InvalidDataError if validation fails
   */
  protected validatePreferencesData(data: UpdateUserPreferencesRequest): void {
    const result = updateUserPreferencesSchema.safeParse(data);

    if (!result.success) {
      throw createValidationError(result.error);
    }
  }
}

// =============================================================================
// SERVICE INSTANCE AND FACTORY
// =============================================================================

/**
 * Default user preferences service instance
 * Pre-configured and ready to use throughout the application
 */
export const userPreferencesService = new UserPreferencesService();

/**
 * Factory function to create a new UserPreferencesService instance
 * Useful for testing or when custom configuration is needed
 *
 * @param cacheTTL - Optional custom cache TTL in milliseconds
 * @returns A new UserPreferencesService instance
 */
export function createUserPreferencesService(
  cacheTTL?: number
): UserPreferencesService {
  return new UserPreferencesService(cacheTTL);
}

export default UserPreferencesService;
